import { StartAndFinish } from "@cervval.js/calendars/src/calendars.helper";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { ALIGN_ITEMS, FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, FLEX_GAP, FLEX_ITEM_AUTO, JUSTIFY_CONTENT, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { ButtonSize, ButtonStyle, CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsSmartTable, SmartTableCell } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTable";
import { SmartTableColumn, getSmartTablePropertyColumnsFromPropertyConfigs } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTableColumn.tools";
import { EDITOR_FACTORY_SERVICE, EditorFactoryService } from "@cervval.js/client-html5-ui/src/editors/editorFactory.service";
import { EditorWithPicker } from "@cervval.js/client-html5-ui/src/editors/editorsWithPicker/editorWithPicker";
import { CalendarCellStyle } from "@cervval.js/client-html5-ui/src/pickers/calendar/calendar";
import { CjsDateIntervalPicker } from "@cervval.js/client-html5-ui/src/pickers/dateIntervalPicker/dateIntervalPicker";
import { CatchErrorWithDialog } from "@cervval.js/client-html5-ui/src/tools/catchErrorWithDialog.decorator";
import { INIT_ID } from "@cervval.js/common-action/src/action.tools";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getLogConsole, getLogService } from "@cervval.js/common/src/services/log.service";
import { hexToRgb, negativeBlackOrWhiteHex } from "@cervval.js/common/src/tools/color.tools";
import { keyof } from "@cervval.js/common/src/tools/object.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { TODAY } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { add, toDate } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { showHideRowButtonsOnHover } from "@ehealth_tools/client-html-tools/src/tools/smartTable.tools";
import { getCalendarExceptionPropertyConfig } from "@ehealth_tools/common-booking/src/configs/exception.propertyConfig";
import { BookingCalendar, BookingException, BookingWeek } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { AbstractCommonBookingService, BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { filterByDateInterval, getActiveException } from "@ehealth_tools/common-booking/src/tools/calendar.tools";
import { deepClone } from "@ehealth_tools/common/src/tools/object.tools";
import { AbstractClientBookingService } from "../../../../../../services/abstractClientBooking.service";
import { TimeSlotsEditor } from "../timeslotsEditor/timeslotsEditor";
import { WeekSlots } from "../weekSlot/weekSlots.component";
import { I18nException } from "./calendarException.component.i18n";

export interface CalendarExceptionOptions {
    currentCalendar?: BookingCalendar;
}

const defaultCalendarExceptionOptions: CalendarExceptionOptions = {
    //
};


@CustomElement({
    name: "calendar-exception",
    template: require("./calendarException.component.html"),
    style: require("./calendarException.component.scss"),
    i18n: require("./calendarException.component.i18n").I18n,
    elementCssClasses: [FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, W_100, FLEX_GAP.GAP_2]
})
export class CalendarException<I18n extends I18nException = I18nException> extends WebComponent<I18n> {

    @TypedWebComponentOptions(defaultCalendarExceptionOptions)
    public readonly options!: WebComponentOptions<CalendarExceptionOptions>;

    @TypedHtmlElements({ ensureLoadedClasses: [WeekSlots, TimeSlotsEditor, CjsSmartTable, CjsDateIntervalPicker] })
    private _components!: {
        dateIntervalPicker: CjsDateIntervalPicker,
        exceptionsWeekSlots: WeekSlots,
        addException: CjsButton,
        exceptionList: CjsSmartTable<BookingException>,
        exceptionTimeSlotsEditor: TimeSlotsEditor
    };


    @Injected(EDITOR_FACTORY_SERVICE)
    private _editorFactoryService!: EditorFactoryService;

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;


    private _calendarPickerSelection: Partial<StartAndFinish> = {};
    private _displayedMonth: UnixTimestamp | undefined;

    /** Init component */
    public override init(): void {

        this._components.exceptionsWeekSlots.options.setAndRefreshIfChanged({
            onSelectDay: (days) => {
                const selectedCalendar = this.options.currentCalendar;
                if (!selectedCalendar) {
                    // getLogService().error("no selected calendar");
                    return;
                }
                this._components.exceptionTimeSlotsEditor.options.setAndRefreshIfChanged({ selectedDays: days });
            }
        });

        this._components.dateIntervalPicker.valueChangeEvent.on(e => {
            const selectedCalendar = this.options.currentCalendar;
            if (!selectedCalendar) {
                // getLogService().error("no selected calendar");
                return;
            }
            const dateInterval = this._components.dateIntervalPicker.getValueInterval();
            if (!dateInterval) {
                getLogService().error("undefined dateInterval");
                return;
            }
            const start = dateInterval.start;
            const finish = dateInterval.finish;
            if (start && finish) {
                this._calendarPickerSelection = { start, finish };
            } else {
                this._calendarPickerSelection = {};
            }
            this._refreshList();
        });

        // TODO dirty: should be bound on click
        this._components.dateIntervalPicker.addEventListener("mouseup", (e) => {
            setTimeout(() => {
                if (this._components.dateIntervalPicker.currentTs !== this._displayedMonth) {
                    this.refresh();
                }
            }, 0);
        });

        // Exception List
        this._components.addException.options.setAndRefreshIfChanged({
            icon: { name: "fa-plus" },
            iconMode: "only",
            cssClasses: ["w-100", "addException"]
        });

        this._components.addException.clickEvent.on(async () => {
            try {

                const selectedCalendar = this.options.currentCalendar;
                if (selectedCalendar) {
                    const newWeek = deepClone(AbstractCommonBookingService.defaultWeek);
                    await this._bookingService.actionService.encapsulateInAction(action => {
                        action.create(this._bookingService.tables.bookingWeeks, newWeek);

                        const start = this._calendarPickerSelection.start && this._calendarPickerSelection.finish ? this._calendarPickerSelection.start < this._calendarPickerSelection.finish ? this._calendarPickerSelection.start : this._calendarPickerSelection.finish : undefined;
                        const finish = this._calendarPickerSelection.start && this._calendarPickerSelection.finish ? this._calendarPickerSelection.start < this._calendarPickerSelection.finish ? this._calendarPickerSelection.finish : this._calendarPickerSelection.start : undefined;
                        action.create(this._bookingService.tables.bookingExceptions, <BookingException>{
                            id: INIT_ID,
                            start: start ?? TODAY(),
                            finish: finish ?? toDate(TODAY()).beginOfNext("day").uts,
                            weekId: newWeek.id,
                            calendarId: selectedCalendar.id
                        });
                    }, { logMessage: "Create new week" }).then(_ => {
                        this.refresh();
                    }).catch(CatchErrorWithDialog);


                }
            } catch (e) {
                getLogService().error(e);
            }
        });

        const columns: SmartTableColumn<BookingException>[] = getSmartTablePropertyColumnsFromPropertyConfigs(getCalendarExceptionPropertyConfig());
        const finishCol = columns.find(v => v.uid === keyof<BookingException>("finish"));
        if (finishCol) {
            finishCol.getCellValue = (cell) => {
                const item = cell.rowItemData;
                if (item) {
                    const previousDay = toDate(item.finish).beginOfPrevious("day");
                    return previousDay.uts;
                }
                return undefined;
            };
        }

        for (const column of columns) {
            column.getCellEditor = (cell) => {
                if (!cell.column.propertyConfig) { return undefined; }
                const editor = this._editorFactoryService.newEditor(cell.column.propertyConfig);
                if (editor instanceof EditorWithPicker) {
                    editor.options.setWithoutRefresh({
                        popupContainer: document.body
                    });
                }
                return editor;
            };
        }

        columns.push({
            uid: "actions",
            width: "10%",
            filterDisabled: true,
            sortDisabled: true,
            renderCell: (cell: SmartTableCell<BookingException>) => {
                const exception = cell.rowItemData;
                if (exception) {
                    const deleteButton = new CjsButton({
                        icon: { name: "fa-trash" },
                        cssStyle: ButtonStyle.DANGER,
                        size: ButtonSize.SMALL,
                        iconMode: "only"
                    });
                    deleteButton.clickEvent.on(() => {
                        this._bookingService.actionService.encapsulateInAction(action => {
                            const exceptionWeek = this._bookingService.getById<BookingWeek>("bookingWeeks", exception.weekId);
                            action.delete(this._bookingService.tables.bookingWeeks, exceptionWeek);
                            action.delete(this._bookingService.tables.bookingExceptions, exception);
                        }, { logMessage: "Delete exception" }).then(_ => this.refresh()).catch(CatchErrorWithDialog);
                    });
                    const row = cell.row.tr;
                    if (row) {
                        showHideRowButtonsOnHover(row, [deleteButton]);
                    }
                    return createHtmlElement("div", {
                        cssClass: [FLEX_CONTAINER, W_100, FLEX_ITEM_AUTO, JUSTIFY_CONTENT.CENTER, ALIGN_ITEMS.CENTER],
                        innerHTML: [deleteButton]
                    });
                } else {
                    return null;
                }
            }
        });

        this._components.exceptionList.options.setWithoutRefresh({
            columns,
            isCellEditable: (c) => true
        });

        this._components.exceptionList.rowClickEvent.on(e => {
            const exception = e.detail.cell.rowItemData;
            if (exception) {
                this._components.dateIntervalPicker.setValueInterval({ start: exception.start, finish: exception.finish });
                const week = this._bookingService.getById<BookingWeek>("bookingWeeks", exception.weekId);
                this._components.exceptionsWeekSlots.options.setAndRefreshIfChanged({ currentWeek: week });
                this._components.exceptionTimeSlotsEditor.options.setAndRefreshIfChanged({ currentWeek: week });
            }
        });

        this._components.exceptionList.cellEditedValueEvent.on(async (e) => {
            const key = e.detail.cell.column.uid;
            const dto = e.detail.cell.rowItemData;
            let value = e.detail.newValue;
            let extraKey = "";
            let extraValue: UnixTimestamp | undefined = undefined;
            if (dto) {
                if (key === "color") {
                    const rgb = hexToRgb(value);
                    value = `rgb(${rgb?.r},${rgb?.g},${rgb?.b})`;
                }
                if (key === "finish") {
                    if (value < dto.start) {
                        extraKey = "start";
                        extraValue = value;
                        value = dto.start;
                    }
                    value = toDate(value).beginOfNext("day").uts;
                }
                if (key === "start" && value > dto.finish) {
                    extraKey = "finish";
                    extraValue = value;
                    value = dto.finish;
                }
                try {
                    await this._bookingService.actionService.encapsulateInAction(action => {
                        action.updateValuesIfNeeded(this._bookingService.tables.bookingExceptions, dto, { [key]: value, [extraKey]: extraValue });
                    }, { logMessage: "Update exception" });
                    this._refreshDatePicker();
                } catch (err) {
                    getLogConsole().error(err);
                }
            }
        });

        this._components.exceptionTimeSlotsEditor.options.setAndRefreshIfChanged({
            onChange: () => { 
                this._components.exceptionsWeekSlots.refresh();
            }
        });
        this._components.exceptionsWeekSlots.options.setAndRefreshIfChanged({
            onSelectDay: (days) => {
                this._components.exceptionTimeSlotsEditor.options.setAndRefreshIfChanged({ selectedDays: days });
            }
        });
    }

    /** Refresh component */
    public override refresh(reason?: string): void {
        this._components.exceptionsWeekSlots.options.setAndRefreshIfChanged({ currentWeek: undefined });
        this._components.exceptionTimeSlotsEditor.options.setAndRefreshIfChanged({ currentWeek: undefined });

        this._refreshDatePicker();
        this._refreshList();
    }

    /** Refresh date picker with current exceptions of the month */
    private _refreshDatePicker(): void {
        this._displayedMonth = this._components.dateIntervalPicker.currentTs;
        const calendarStyle: CalendarCellStyle[] = [];
        const exceptionsOfTheMonth = filterByDateInterval(this._bookingService.getData<BookingException>("bookingExceptions").filter(v => v.calendarId === this.options.currentCalendar?.id),
            { start: this._components.dateIntervalPicker.currentDate.beginOf("month").uts, finish: this._components.dateIntervalPicker.currentDate.beginOfNext("month").uts });
        for (let d = -15; d < 31 + 15; d++) {
            const day = add(this._components.dateIntervalPicker.currentDate.beginOf("month"), { day: d });

            if (getActiveException(day.uts, exceptionsOfTheMonth)) {
                const color = "#eee";
                calendarStyle.push({
                    date: day.uts,
                    style: {
                        backgroundColor: color,
                        color: negativeBlackOrWhiteHex(color)
                    }
                });
            }
        }
        if (this.options.currentCalendar != null) {
            this._components.dateIntervalPicker.options.setWithoutRefresh({ calendarCellStyles: calendarStyle });
        }
        this._components.dateIntervalPicker.refresh();
    }

    /** Refresh exception list with currentCalendar data */
    private _refreshList(): void {
        if (this.options.currentCalendar != null) {
            this._components.exceptionList.setData({
                data: filterByDateInterval(this._bookingService.getData<BookingException>("bookingExceptions").filter(v => v.calendarId === this.options.currentCalendar?.id),
                    { start: this._components.dateIntervalPicker.currentDate.beginOf("month").uts, finish: this._components.dateIntervalPicker.currentDate.beginOfNext("month").uts })
            });
        } else {
            this._components.exceptionList.setData({ data: [] });
        }
    }
}